import React, { useEffect, useState } from 'react';
import { Input, Select, DatePicker, Tooltip, Upload } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { EmptyIcon } from 'assets/icon/bigicon';
import { ExportIcon, TrashIcon } from 'assets/icon/dynamic';
import { fetchMasterConfigs } from 'state/actions/masterConfig';
import { fetchChildCategories } from 'state/actions/childComponentCategory';
import { fetchPois } from 'state/actions/pois';
import {
  createChildComponent,
  updateChildComponent,
} from 'state/actions/childComponent';
import { imgUrl } from 'utils/url';
import dayjs from 'dayjs';

function AddChildView({ onBack, editData, onMasterConfig }) {
  const isEditing = !!editData;
  const [childTitle, setChildTitle] = useState('');
  const [categoryIds, setCategoryIds] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [draggedField, setDraggedField] = useState(null);
  const [filePreviews, setFilePreviews] = useState({});

  const dispatch = useDispatch();
  const { siteId } = useParams();

  const { list, categoriesList, poisList } = useSelector((state) => ({
    list: state.masterConfig.list,
    categoriesList: state.childComponentCategory.list,
    poisList: state.pois.list,
  }));

  useEffect(() => {
    if (siteId) {
      dispatch(fetchMasterConfigs({ siteId }));
      dispatch(fetchChildCategories({ siteId }));
      dispatch(fetchPois({ siteId, categoryId: 'all', type: 'published' }));
    }
  }, [siteId, dispatch]);

  const masterConfig = list?.[0];
  const fields = [...(masterConfig?.fields || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  useEffect(() => {
    if (editData && fields.length > 0) {
      setChildTitle(editData.childTitle || '');
      setCategoryIds(
        (editData.categories || []).map((c) => c.categoryId)
      );
      const values = {};
      const previews = {};
      const dynamicFields = editData.dynamicFields || [];
      fields.forEach((field) => {
        const dynField = dynamicFields.find((df) => df.fieldId === field._id);
        if (!dynField || dynField.value === undefined) return;
        const val = dynField.value;
        if (field.fieldType === 'MediaField' && val && val.location) {
          values[field._id] = val;
          previews[field._id] = `${imgUrl}${val.location}`;
        } else if (field.fieldType === 'DatePicker' && val && val.startDate) {
          values[field._id] = {
            startDate: dayjs(val.startDate),
            endDate: dayjs(val.endDate),
          };
        } else {
          values[field._id] = val;
        }
      });
      setFieldValues(values);
      setFilePreviews(previews);
    }
  }, [editData, fields.length]);

  const handleFieldChange = (fieldId, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleFileChange = (fieldId, file) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: file }));
    setFilePreviews((prev) => ({
      ...prev,
      [fieldId]: URL.createObjectURL(file),
    }));
    return false;
  };

  const handleRemoveFile = (fieldId) => {
    if (filePreviews[fieldId]) {
      URL.revokeObjectURL(filePreviews[fieldId]);
    }
    setFieldValues((prev) => ({ ...prev, [fieldId]: null }));
    setFilePreviews((prev) => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!childTitle || !masterConfig) return;

    const dynamicData = {};
    const mediaFiles = {};

    fields.forEach((field) => {
      const value = fieldValues[field._id];
      if (value === undefined || value === null) return;

      if (field.fieldType === 'MediaField') {
        if (value instanceof File) {
          mediaFiles[field._id] = value;
        } else if (value.location) {
          dynamicData[field._id] = value;
        }
      } else {
        dynamicData[field._id] = value;
      }
    });

    if (isEditing) {
      dispatch(
        updateChildComponent(
          {
            id: editData._id,
            masterConfigId: masterConfig._id,
            childTitle,
            childComponentCategoryIds: categoryIds,
            dynamicData,
            mediaFiles,
          },
          () => onBack()
        )
      );
    } else {
      dispatch(
        createChildComponent(
          {
            masterConfigId: masterConfig._id,
            childTitle,
            childComponentCategoryIds: categoryIds,
            dynamicData,
            mediaFiles,
          },
          () => onBack()
        )
      );
    }
  };

  const renderField = (field) => {
    switch (field.fieldType) {
      case 'TextField':
        return (
          <Input
            placeholder={`Enter ${field.label}`}
            value={fieldValues[field._id] || ''}
            onChange={(e) => handleFieldChange(field._id, e.target.value)}
          />
        );

      case 'MediaField':
        return fieldValues[field._id] && filePreviews[field._id] ? (
          <div className="upload-image-preview">
            <Tooltip placement="top" title="Delete">
              <button
                type="button"
                onClick={() => handleRemoveFile(field._id)}
                className="btn-remove"
              >
                <TrashIcon size={18} color="#CB3A31" />
              </button>
            </Tooltip>
            {(fieldValues[field._id] instanceof File
              ? fieldValues[field._id].type === 'video/mp4'
              : fieldValues[field._id].fileName?.endsWith('.mp4')) ? (
              <video
                controls
                src={filePreviews[field._id]}
                style={{ width: '100%' }}
              />
            ) : (
              <img
                className="media-avatar"
                src={filePreviews[field._id]}
                alt={fieldValues[field._id].name}
              />
            )}
          </div>
        ) : (
          <Upload
            accept="image/*,video/mp4"
            showUploadList={false}
            beforeUpload={(file) => handleFileChange(field._id, file)}
          >
            <div
              onDragLeave={() => setDraggedField(null)}
              onDragOver={() => setDraggedField(field._id)}
              className={`upload-drag-and-drop ${
                draggedField === field._id ? 'active' : ''
              }`}
            >
              <ExportIcon size={22} color="#C2C2C2" />
              <p className="upload-title">
                Drag here or <span className="upload-highlight">Browse</span>
              </p>
              <p className="upload-subtitle">
                File format supported: jpg, png, mp4 (Up to 50MB)
              </p>
            </div>
          </Upload>
        );

      case 'DatePicker':
        return (
          <div className="dynamic-comp-datepicker-row">
            <DatePicker
              placeholder="Start Date"
              format="DD/MM/YYYY HH:mm"
              showTime
              style={{ width: '100%' }}
              value={fieldValues[field._id]?.startDate || null}
              onChange={(date) =>
                handleFieldChange(field._id, {
                  ...(fieldValues[field._id] || {}),
                  startDate: date,
                })
              }
            />
            <DatePicker
              placeholder="End Date"
              format="DD/MM/YYYY HH:mm"
              showTime
              style={{ width: '100%' }}
              value={fieldValues[field._id]?.endDate || null}
              onChange={(date) =>
                handleFieldChange(field._id, {
                  ...(fieldValues[field._id] || {}),
                  endDate: date,
                })
              }
            />
          </div>
        );

      case 'Poi':
        return (
          <Select
            mode="multiple"
            showSearch
            placeholder={`Select ${field.label}`}
            style={{ width: '100%' }}
            value={fieldValues[field._id] || []}
            onChange={(value) => handleFieldChange(field._id, value)}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children || '')
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {[...poisList]
              .sort((a, b) =>
                (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
              )
              .map((poi) => (
              <Select.Option key={poi._id} value={poi._id}>
                {poi.name}
              </Select.Option>
              ))}
          </Select>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dynamic-comp">
      <div className="dynamic-comp-header">
        <h2>{isEditing ? 'Edit Child' : 'Add New Child'}</h2>
        <button className="dynamic-comp-btn-back" onClick={onBack}>
          <i className="feather-arrow-left" />
          Back
        </button>
      </div>

      {!masterConfig || fields.length === 0 ? (
        <div className="dynamic-comp-empty">
          <EmptyIcon className="dynamic-comp-empty-icon" />
          <h3>No Master Data</h3>
          <p>
           Make sure the Master Config is fully established prior to generating any Child instances
          </p>
          <button
            className="dynamic-comp-btn-new-child"
            onClick={onMasterConfig}
            style={{ marginTop: 16 }}
          >
            <i className="feather-settings" />
            Master Config
          </button>
        </div>
      ) : (
      <div className="dynamic-comp-child-center">
        <div className="poi-form">
            <div className="form">
              <label className="form-label">Child Title</label>
              <Input
                placeholder="Enter Child Title"
                value={childTitle}
                onChange={(e) => setChildTitle(e.target.value)}
              />
            </div>

            {categoriesList.length > 0 && (
              <div className="form">
                <label className="form-label">Category</label>
                <Select
                  mode="multiple"
                  placeholder="Select Categories"
                  value={categoryIds}
                  onChange={(val) => setCategoryIds(val)}
                  style={{ width: '100%' }}
                >
                  {categoriesList.map((cat) => (
                    <Select.Option key={cat._id} value={cat._id}>
                      {cat.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {fields.map((field) => (
              <div
                key={field._id}
                className={`form${
                  field.fieldType === 'DatePicker' && field.setAsScheduling
                    ? ' dynamic-comp-child-field--datepicker'
                    : ''
                }`}
              >
                <label className="form-label">
                  {field.label}
                  {field.description && (
                    <Tooltip title={field.description}>
                      <i className="feather-info dynamic-comp-info-icon" />
                    </Tooltip>
                  )}
                  {field.fieldType === 'DatePicker' && field.setAsScheduling && (
                    <span className="dynamic-comp-scheduling-ref">
                      Scheduling Reference
                    </span>
                  )}
                </label>
                {renderField(field)}
              </div>
            ))}

            <div className="dynamic-comp-child-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={onBack}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`dynamic-comp-btn-submit${
                  childTitle ? ' active' : ''
                }`}
                disabled={!childTitle}
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default AddChildView;
